import {Alert, Scrollbar, Table} from '@forecast-it/design-system';
import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {graphql, useFragment} from 'react-relay';
import {MODULE_TYPES} from '../../../../constants';
import DeleteCustomFieldDefinitionMutation from '../../../../mutations/delete_custom_field_definition_mutation';
import tracking from '../../../../tracking';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {hasModule} from '../../../shared/util/ModuleUtil';
import Util from '../../../shared/util/util';
import {EntityType} from '../Constants';
import {showDeleteCustomFieldConfirmationModal} from '../modals/DeleteCustomFieldConfirmationModal';
import {CustomFieldDefinition} from '../types';
import type {CustomFieldTable_company$key as CustomFieldTable_company} from './__generated__/CustomFieldTable_company.graphql';
import CustomFieldsEmptyState from './CustomFieldsEmptyState';

const CustomFieldTable_companyFragment = graphql`
	fragment CustomFieldTable_company on Company {
		id
		customFieldDefinitionsV2(first: 1000) @connection(key: "Company_customFieldDefinitionsV2") {
			edges {
				node {
					id
					key
					type
					displayName
					entityType
					avaRef
					... on TextCustomField {
						readOnly
					}
					... on IntegerCustomField {
						readOnly
					}
					... on DecimalCustomField {
						readOnly
					}
				}
			}
		}
	}
`;

type CustomFieldTableProps = {
	company: CustomFieldTable_company | null;
	entityType: EntityType;
	onEditCustomField: (id: string) => void;
	onCreateCustomField: () => void;
	setAlertMessage: (AlertMessage) => void;
};

const getTypeDisplayName = (type: string | null | undefined) => {
	switch (type) {
		case 'STRING':
			return 'Free Text';
		case 'DROPDOWN':
			return 'Dropdown';
		case 'INTEGER':
			return 'Integer';
		case 'DECIMAL':
			return 'Decimal';
		default:
			return 'Unknown';
	}
};

type CustomFieldDefinitionRow = CustomFieldDefinition & {
	typeDisplayName: string;
	avaRefName: string;
};

export const CustomFieldTable = ({
	company: companyRef,
	entityType,
	onEditCustomField,
	onCreateCustomField,
	setAlertMessage,
}: CustomFieldTableProps) => {
	const {formatMessage} = useIntl();

	const company = useFragment(CustomFieldTable_companyFragment, companyRef);

	const editCustomFieldDefinition = (customFieldDefinition: CustomFieldDefinitionRow) => {
		tracking.trackEvent('CustomFieldDefinition - Opened');
		trackEvent('Custom Field Definition', 'Opened');
		onEditCustomField(customFieldDefinition.id);
	};

	const deleteCustomFieldDefinition = (customFieldDefinition: CustomFieldDefinitionRow) => {
		Util.CommitMutation(
			DeleteCustomFieldDefinitionMutation,
			{
				id: customFieldDefinition.id,
				companyId: company?.id,
			},
			() => {
				setAlertMessage({
					text: formatMessage({id: 'settings.custom-fields.delete_alert'}, {name: customFieldDefinition.displayName}),
					type: Alert.TYPE.WARNING,
				});
			}
		);
	};
	const confirmDeletion = (customFieldDefinition: CustomFieldDefinitionRow) => {
		tracking.trackEvent('CustomFieldDefinition - Delete confirm modal opened');
		trackEvent('Custom Field Confirm Delete Modal', 'Opened');
		showDeleteCustomFieldConfirmationModal({
			onDelete: () => deleteCustomFieldDefinition(customFieldDefinition),
			customFieldName: customFieldDefinition.displayName,
		});
	};

	const customFieldDefinitionRows: CustomFieldDefinitionRow[] = useMemo(() => {
		const customFieldDefinitions = company?.customFieldDefinitionsV2?.edges?.map(edge => edge?.node) || [];
		return customFieldDefinitions
			.filter(customFieldDefinition => customFieldDefinition?.entityType === entityType)
			.map((definition: CustomFieldDefinition) => ({
				...definition,
				typeDisplayName: getTypeDisplayName(definition?.type),
				avaRefName: definition?.avaRef
					? `${
							String(definition.entityType).charAt(0) + String(definition.entityType.toLowerCase()).slice(1)
					  } Custom Field ${definition.avaRef}`
					: 'None',
				'data-cy': `custom-field-definition-row-${definition.key}`,
			}))
			.sort((a, b) => Util.sortAlphabetically(a?.key, b?.key));
	}, [company]);

	const totalHeightOfHeaders = 436;
	const [maxHeight, setMaxHeight] = useState(window.innerHeight - totalHeightOfHeaders);

	useEffect(() => {
		const handleResize = () => setMaxHeight(window.innerHeight - totalHeightOfHeaders);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	if (customFieldDefinitionRows?.length === 0) {
		return <CustomFieldsEmptyState entityType={entityType} onCreateCustomField={onCreateCustomField} />;
	}

	return (
		<Scrollbar maxHeight={maxHeight}>
			<Table width={'100%'} data={customFieldDefinitionRows}>
				<Table.TextColumn
					accessorKey="displayName"
					allowSorting
					data-cy="name"
					header="Display Name"
					sortingFn="text"
				/>
				<Table.TextColumn width="160px" accessorKey="key" allowSorting data-cy="key" header="Key" sortingFn="text" />
				<Table.TextColumn
					width="160px"
					accessorKey="typeDisplayName"
					allowSorting
					data-cy="type"
					header="Type"
					sortingFn="text"
				/>
				{hasModule(MODULE_TYPES.SISENSE) &&
				(entityType === 'PROJECT' || entityType === 'TASK') &&
				hasFeatureFlag('custom_field_ava_refs') ? (
					<Table.TextColumn
						width="250px"
						accessorKey="avaRefName"
						allowSorting
						data-cy="avaRef"
						header="Advanced Analytics Reference"
					/>
				) : undefined}
				<Table.IconColumn
					width="114px"
					accessorKey="readOnly"
					align={Table.Align.left}
					data-cy="readOnly"
					header="Read Only"
					icon={value =>
						value === true
							? {
									icon: 'tick',
									color: 'default',
							  }
							: undefined
					}
				/>
				<Table.IconColumn
					width="36px"
					accessorKey="edit"
					align={Table.Align.center}
					header=""
					hideHeader
					data-cy="edit"
					icon={() => ({
						icon: 'edit',
						color: 'default',
					})}
					onClick={(definition: CustomFieldDefinitionRow) => {
						editCustomFieldDefinition(definition);
					}}
				/>
				<Table.IconColumn
					width="36px"
					accessorKey="delete"
					align={Table.Align.center}
					header=""
					hideHeader
					data-cy="delete"
					icon={() => ({
						icon: 'trash',
						color: 'default',
					})}
					onClick={(definition: CustomFieldDefinitionRow) => {
						confirmDeletion(definition);
					}}
				/>
			</Table>
		</Scrollbar>
	);
};
